import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { trainingTemplatesAPI } from '../../services/api';
import type { TrainingTemplate } from '../../types/trainingTemplate';
import PlanView from '../trainings/PlanView';
import DrillSheet from './DrillSheet';

export type TemplateLoadMode = 'replace' | 'append';

interface TemplatePickerProps {
  onClose: () => void;
  onLoad: (template: TrainingTemplate, mode: TemplateLoadMode) => void;
}

/** Edzéssablon kiválasztása: a teljes terv lecserélése vagy a blokkok hozzáfűzése. */
const TemplatePicker: React.FC<TemplatePickerProps> = ({ onClose, onLoad }) => {
  const [templates, setTemplates] = useState<TrainingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        setTemplates(await trainingTemplatesAPI.getAll({ search: search || undefined }));
      } catch (err) {
        console.error('Az edzéssablonok betöltése nem sikerült:', err);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <DrillSheet title="Edzéssablon betöltése" onClose={onClose} maxWidthClass="md:max-w-3xl">
      <div className="space-y-4">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés név vagy cél alapján..."
            aria-label="Keresés az edzéssablonok között"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Betöltés...</div>
        ) : templates.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            Még nincs edzéssablon. Egy kész edzéstervnél a „Mentés sablonként” gombbal készíthetsz egyet.
          </div>
        ) : (
          <ul className="space-y-2">
            {templates.map((template) => {
              const expanded = expandedId === template.id;
              return (
                <li key={template.id} className="rounded-md border border-gray-200">
                  <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center">
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : template.id)}
                      aria-expanded={expanded}
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    >
                      {expanded ? (
                        <ChevronUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                      )}
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-gray-900">{template.name}</span>
                        <span className="block text-xs text-gray-500">
                          {[
                            `${template.items.length} blokk`,
                            template.totalMinutes ? `${template.totalMinutes} perc` : null,
                            template.ageGroupHint,
                            template.createdByName,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        {template.goal && <span className="mt-0.5 block truncate text-xs text-gray-500">Cél: {template.goal}</span>}
                      </span>
                    </button>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => onLoad(template, 'append')}
                        className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 sm:flex-none"
                      >
                        Hozzáfűzés
                      </button>
                      <button
                        type="button"
                        onClick={() => onLoad(template, 'replace')}
                        className="flex-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 sm:flex-none"
                      >
                        Betöltés
                      </button>
                    </div>
                  </div>
                  {expanded && (
                    <div className="border-t border-gray-100 bg-gray-50 p-3">
                      <PlanView goal={template.goal} items={template.items} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-xs text-gray-400">
          A „Betöltés” lecseréli a jelenlegi tervet (célt és blokkokat), a „Hozzáfűzés” a blokkokat a meglévők után teszi.
          Mentésig semmi nem változik az edzésen.
        </p>
      </div>
    </DrillSheet>
  );
};

export default TemplatePicker;
